import Router from "next/router";
import * as types from "./jobPostingActionTypes";
import fetchUrl from "../../services/fetchUrl";
import {
  showSnackBar,
  showSuccessSnackBar
} from "../../components/Snackbar/snackbarActions";
import {
  ERROR_MESSAGE,
  STORAGE_ITEMS,
  STORAGE_KEYS
} from "../../services/constants";
import makeAjaxRequest from "../../services/makeAjaxRequest";
import {
  getErrorMessage,
  activeTabFormat,
  detectMobile
} from "../../services/utils";
import { getProfile } from "../Login/rolesActions";
import { setActiveTab } from "../Dashboard/dashboardActions";
import { logEvents } from "../../redux/commonActions";
import {
  JOB_POST_SUCCESS,
  AUTO_SAVE_JOB_POSTED,
  JOB_POST_EDIT
} from "../../services/webengage/webEngageConstants";
import { EMAIL_VERIFICATION } from "../../routes/RouteConstants";
import MetaDataService from "../../services/MetaDataService";
import { checkSessionChanged } from "../../utils/checkSession";
import CacheService from "../../services/CacheService";
import JobPostingService from "../../services/JobPostingService";
import usePostJobSuccessStore from "../../zustandStores/usePostJobSuccessStore";

const checkInCache = async (object, cacheName) => {
  try {
    const sortObject = (await import("smart-deep-sort")).default;
    const sObject = sortObject(object);
    const cache = CacheService.checkInCache({
      key: sObject,
      cacheName,
      storageType: STORAGE_KEYS.LOCAL_STORAGE
    });

    return cache;
  } catch (error) {
    console.log("error checking in cache");
    return null;
  }
};

const isVersionChanged = (store, apiVersion, versionsKey) => {
  const reduxStore = store();
  const { jobPostingReducer = {} } = reduxStore || {};
  const {
    metaDataVersion: {
      payload: metaDataPayload = {},
      isSuccess: metaDataIsSuccess
    } = {}
  } = jobPostingReducer;

  return metaDataIsSuccess && metaDataPayload[versionsKey] !== apiVersion;
};

export const getMetaDataLoading = () => {
  return {
    type: types.GET_JOB_POST_DATA_LOADING
  };
};

export const getMetaDataSuccess = payload => {
  return {
    type: types.GET_JOB_POST_DATA_SUCCESS,
    payload: payload
  };
};

export const getMetaDataFailure = error => {
  return {
    type: types.GET_JOB_POST_DATA_FAILURE,
    payload: error
  };
};

export const getMetaData = (isLoader = true) => {
  return (dispatch, store) => {
    return new Promise((resolve, reject) => {
      if (isLoader) dispatch(getMetaDataLoading());

      const cache = CacheService.checkInCache({
        key: STORAGE_ITEMS.META_JOB_POST_CACHE,
        cacheName: STORAGE_ITEMS.META_JOB_POST_CACHE,
        storageType: STORAGE_KEYS.LOCAL_STORAGE
      });
      if (!cache) {
        makeAjaxRequest({
          method: "GET",
          url: fetchUrl("v2/metadata", "v3/job_post"),
          auth: false
        })
          .then(response => {
            const { data: jobPostData } = response.data;

            CacheService.addToCache({
              data: {
                key: STORAGE_ITEMS.META_JOB_POST_CACHE,
                value: jobPostData
              },
              cacheName: STORAGE_ITEMS.META_JOB_POST_CACHE,
              storageType: STORAGE_KEYS.LOCAL_STORAGE,
              ttl: 720
            });

            dispatch(getMetaDataSuccess(jobPostData));
            resolve(response.data.data);
          })
          .catch(error => {
            dispatch(getMetaDataFailure(error.message));
            dispatch(showSnackBar(ERROR_MESSAGE));
            reject(error);
          });
      } else {
        dispatch(getMetaDataSuccess(cache));
        resolve(true);
      }
    });
  };
};

export const getFamousLocationMetaDataSuccess = payload => {
  return {
    type: types.FAMOUS_LOCATION_METADATA_SUCCESS,
    payload: payload
  };
};

export const getFamousLocationMetaDataFailure = error => {
  return {
    type: types.FAMOUS_LOCATION_METADATA_FAILURE,
    payload: error
  };
};

export const getFamousLocationMetaDataLoading = () => {
  return {
    type: types.FAMOUS_LOCATION_METADATA_LOADING
  };
};

export const getFamousLocationMetaData = params => {
  const { isLoader = true, cityId, localityId, categoryId } = params;
  return dispatch => {
    if (isLoader) dispatch(getFamousLocationMetaDataLoading());
    makeAjaxRequest({
      method: "GET",
      url: fetchUrl("v2/metadata", "famous-location"),
      queryParams: {
        city_id: cityId,
        locality_id: localityId,
        category_id: categoryId
      },
      auth: false
    })
      .then(response => {
        const { data } = response.data;
        dispatch(getFamousLocationMetaDataSuccess(data));
      })
      .catch(error => {
        dispatch(getFamousLocationMetaDataFailure(error.message));
        dispatch(showSnackBar(ERROR_MESSAGE));
      });
  };
};

export const getCityList = () => {
  return (dispatch, store) => {
    return new Promise((resolve, reject) => {
      const reduxStore = store();
      const { jobPostingReducer = {} } = reduxStore || {};
      const {
        metaDataVersion: {
          payload: metaDataPayload = {},
          isSuccess: metaDataIsSuccess
        } = {},
        city = {}
      } = jobPostingReducer;
      const { version: cityCurrentVersion } = city || {};

      if (metaDataIsSuccess && metaDataPayload.cities === cityCurrentVersion) {
        const cities = { options: city.payload, version: city.version };
        const copy = JSON.parse(JSON.stringify(cities));
        dispatch(getCityListSuccess({ cities: copy }));
        return resolve(copy);
      }
      makeAjaxRequest({
        method: "GET",
        url: fetchUrl("v2/metadata/recruiter", "cities"),
        auth: false
      })
        .then(response => {
          dispatch(getCityListSuccess(response.data.data));
          resolve(response.data.data);
        })
        .catch(error => {
          dispatch(getCityListFailure(error.message));
          dispatch(showSnackBar(ERROR_MESSAGE));
          reject(error);
        });
    });
  };
};

export const getCityListLoading = () => {
  return {
    type: types.GET_CITY_LOADING
  };
};

export const getCityListSuccess = payload => {
  return {
    type: types.GET_CITY_SUCCESS,
    payload: payload.cities
  };
};

export const getCityListFailure = error => {
  return {
    type: types.GET_CITY_FAILURE,
    payload: error
  };
};

export const getLocalities = ({ _id }, isLoader = true) => {
  return dispatch => {
    if (isLoader) dispatch(getLocalitiesLoading());
    makeAjaxRequest({
      method: "GET",
      url: fetchUrl("v2/metadata", "recruiter_localities_distance"),
      queryParams: { city_id: _id },
      auth: false
    })
      .then(response => {
        dispatch(getLocalitiesSuccess(response.data.data));
      })
      .catch(error => {
        dispatch(getLocalitiesFailure(error.message));
        dispatch(showSnackBar(ERROR_MESSAGE));
      });
  };
};

export const getLocalitiesLoading = () => {
  return {
    type: types.GET_LOCALITY_LOADING
  };
};

export const getLocalitiesSuccess = payload => {
  return {
    type: types.GET_LOCALITY_SUCCESS,
    payload: payload
  };
};

export const getLocalitiesFailure = error => {
  return {
    type: types.GET_LOCALITY_FAILURE,
    payload: error
  };
};

export const uploadFile = (fileData, route) => {
  return dispatch => {
    dispatch(uploadFileLoading());
    makeAjaxRequest({
      method: "POST",
      url: fetchUrl("recruiter", route),
      data: fileData
    })
      .then(response => {
        const data = response.data.data;
        dispatch(uploadFileSuccess(data));
      })
      .catch(error => {
        dispatch(uploadFileFailure(error.message));
        dispatch(showSnackBar(ERROR_MESSAGE));
      });
  };
};

export const uploadFileLoading = () => {
  return {
    type: types.UPLOAD_FILE_LOADING
  };
};

export const uploadFileSuccess = payload => {
  return {
    type: types.UPLOAD_FILE_SUCCESS,
    payload: payload
  };
};

export const uploadFileFailure = error => {
  return {
    type: types.UPLOAD_FILE_FAILURE,
    payload: error
  };
};

export const postJob = dataObj => {
  const {
    jobDetails,
    isAutoJobPosted = false,
    old_job_id = null,
    allowDuplicate = false,
    callback,
    repost_campaign
  } = dataObj;

  const { category_id = "" } = jobDetails;
  return (dispatch, store) => {
    return new Promise((resolve, reject) => {
      dispatch(clearPostJob());
      dispatch(postJobLoading());
      makeAjaxRequest({
        method: "POST",
        url: fetchUrl("recruiter", "job/v5/save"),
        data: { ...jobDetails },
        customHeaders: {
          "device-source": detectMobile() ? "M-site" : "Desktop"
        },
        allowDuplicate
      })
        .then(response => {
          const {
            tab = "live",
            job_id = "",
            isDraft = false,
            isScreeningApplicable = false,
            communication = ""
          } = response.data.data;
          dispatch(postJobSuccess({ job_id, isScreeningApplicable }, isDraft));
          let activeTab = activeTabFormat(tab);

          dispatch(setActiveTab(activeTab));
          usePostJobSuccessStore.getState().setResponseData({
            job_id,
            isScreeningApplicable,
            category_id,
            tab,
            communication
          });
          let eventInfo = {};
          const reduxStore = store();
          const jobPostEligibility =
            reduxStore.profileReducer.jobPostEligibility.payload;
          const { job_distinction = "" } = jobPostEligibility;
          let jobType = "free";
          if (job_distinction === "extra") {
            jobType = "paid";
          } else if (jobDetails.is_repost) {
            jobType = "repost";
          }

          if (jobDetails.is_repost) {
            eventInfo = {
              ...jobDetails,
              new_job: job_id,
              old_job: old_job_id
            };
          } else {
            eventInfo = { ...jobDetails };
          }
          const eventName = jobDetails.job_id
            ? JOB_POST_EDIT
            : JOB_POST_SUCCESS;
          if (!jobDetails.job_id) {
            // adding this for job limit implementation
            eventInfo = {
              ...eventInfo,
              job_post_type: jobType
            };
          }
          if (repost_campaign) {
            eventInfo = {
              ...eventInfo,
              from_repost_campaigin: !!repost_campaign
            };
          }
          dispatch(logEvents(eventName, eventInfo));
          if (isAutoJobPosted) {
            dispatch(
              logEvents(AUTO_SAVE_JOB_POSTED, {
                ...jobDetails
              })
            );
          }
          return resolve({ job_id, isDraft });
        })
        .catch(error => {
          // callback();
          let errorMessage = "";
          errorMessage = getErrorMessage(error);
          const errorResponse = error && error.response;
          const { details = {}, status = "" } =
            errorResponse && errorResponse.data ? errorResponse.data : {};
          dispatch(postJobFailure({ errorMessage, details }));
          if (status !== 409) dispatch(showSnackBar(errorMessage));
          return reject({ error, details });
        });
    });
  };
};

export const clearPostJob = () => {
  usePostJobSuccessStore.getState().clearResponseData();
  return {
    type: types.CLEAR_POST_JOB
  };
};

export const postJobLoading = () => {
  return {
    type: types.POST_JOB_LOADING
  };
};

export const postJobSuccess = (payload, isDraft) => {
  return {
    type: types.POST_JOB_SUCCESS,
    payload: payload,
    isDraft
  };
};

export const postJobFailure = error => {
  return {
    type: types.POST_JOB_FAILURE,
    payload: error
  };
};

export const editProfile = (profileDetails, callback, isLoader = true) => {
  return dispatch => {
    if (isLoader) {
      dispatch(editProfileLoading());
    }
    makeAjaxRequest({
      method: "POST",
      url: fetchUrl("recruiter", "edit-profile"),
      data: { ...profileDetails }
    })
      .then(response => {
        const data = response.data.data;
        dispatch(editProfileSuccess(data));
        if (callback) callback(profileDetails);
      })
      .then(() => {
        if (window.webengage && "is_whatsapp_enabled" in profileDetails) {
          webengage.user.setAttribute({
            whatsappOptIn: profileDetails.is_whatsapp_enabled
          });
        }
      })
      .then(() => {
        if (isLoader) {
          dispatch(getProfile({ hardCheck: true }));
        } else {
          dispatch(
            getProfile({
              page: null,
              queryJobId: null,
              emailError: null,
              isLoader: false,
              hardCheck: true
            })
          );
        }
      })
      .catch(error => {
        let errorMessage = "";
        errorMessage = getErrorMessage(error);
        dispatch(editProfileFailure(errorMessage));
        dispatch(showSnackBar(errorMessage));
      });
  };
};

export const editProfileLoading = () => {
  return {
    type: types.EDIT_PROFILE_LOADING
  };
};

export const editProfileSuccess = payload => {
  return {
    type: types.EDIT_PROFILE_SUCCESS,

    payload: payload
  };
};

export const editProfileFailure = error => {
  return {
    type: types.EDIT_PROFILE_FAILURE,
    payload: error
  };
};

export const postJobClicked = value => {
  return dispatch => {
    dispatch({
      type: types.POST_JOB_CLICKED,
      payload: value
    });
  };
};

export const saveJobClicked = value => {
  return dispatch => {
    dispatch({
      type: types.SAVE_JOB_CLICKED,
      payload: value
    });
  };
};

export const saveManuallyClicked = value => {
  return dispatch => {
    dispatch({
      type: types.SAVE_MANUALLY_CLICKED,
      payload: value
    });
  };
};

export const getJob = (jobId, isLoader = true, source) => {
  return async dispatch => {
    return new Promise((resolve, reject) => {
      if (jobId) {
        if (isLoader) {
          dispatch(getJobLoading());
        }
        makeAjaxRequest({
          method: "POST",
          url: fetchUrl("recruiter/job/v2", "get-job"),
          data: { job_id: jobId, source: source ? source : undefined }
        })
          .then(response => {
            const data = response.data.data;

            dispatch(getJobSuccess(data));
            return resolve(data); // resolve the promise with the job data
          })
          .catch(error => {
            dispatch(getJobFailure(error.message));
            const msg = error?.response?.data?.error;
            dispatch(showSnackBar(msg ? msg : ERROR_MESSAGE));
            return reject(error); // reject the promise with the error message
          });
      } else {
        resolve(true);
      }
    });
  };
};

export const clearJobData = () => {
  return {
    type: types.CLEAR_JOB_DATA
  };
};

export const getJobLoading = () => {
  return {
    type: types.GET_JOB_LOADING
  };
};

export const getJobSuccess = payload => {
  return {
    type: types.GET_JOB_SUCCESS,
    payload: payload
  };
};

export const getJobFailure = error => {
  return {
    type: types.GET_JOB_FAILURE,
    payload: error
  };
};

export const getJobsHome = () => {
  return dispatch => {
    dispatch(getJobsHomeLoading());
    makeAjaxRequest({
      method: "GET",
      url: fetchUrl("recruiter", "job/v2/dashboard"),
      auth: true
    })
      .then(response => {
        const data = response.data.data;
        dispatch(getJobsHomeSuccess(data));
      })
      .catch(error => {
        dispatch(getJobsHomeFailure(error.message));
        dispatch(showSnackBar(ERROR_MESSAGE));
      });
  };
};

export const getJobsHomeLoading = () => {
  return {
    type: types.GET_JOBS_HOME_LOADING
  };
};

export const getJobsHomeSuccess = payload => {
  return {
    type: types.GET_JOBS_HOME_SUCCESS,
    payload: payload
  };
};

export const getJobsHomeFailure = error => {
  return {
    type: types.GET_JOBS_HOME_FAILURE,
    payload: error
  };
};

/* Start title/category Suggestion */

export const getJobTitles = (isLoader = true) => {
  return (dispatch, store) => {
    return new Promise((resolve, reject) => {
      if (isLoader) dispatch(getJobTitleLoading());

      const cache = CacheService.checkInCache({
        key: STORAGE_ITEMS.JOB_TITLES_CACHE,
        cacheName: STORAGE_ITEMS.JOB_TITLES_CACHE,
        storageType: STORAGE_KEYS.LOCAL_STORAGE
      });

      if (!cache) {
        makeAjaxRequest({
          method: "GET",
          url: fetchUrl("v2/metadata", "job_titles"),
          auth: false
        })
          .then(response => {
            const { data: jobTitlesData } = response.data;

            CacheService.addToCache({
              data: {
                key: STORAGE_ITEMS.JOB_TITLES_CACHE,
                value: jobTitlesData
              },
              cacheName: STORAGE_ITEMS.JOB_TITLES_CACHE,
              storageType: STORAGE_KEYS.LOCAL_STORAGE,
              ttl: 720
            });

            dispatch(getJobTitleSuccess(jobTitlesData));

            return resolve(jobTitlesData);
          })
          .catch(error => {
            dispatch(getJobTitleFailure(error.message));
            dispatch(showSnackBar(ERROR_MESSAGE));
            return reject(error);
          });
      } else {
        dispatch(getJobTitleSuccess(cache));
        return resolve(true);
      }
    });
  };
};

export const getJobTitleLoading = () => {
  return {
    type: types.GET_JOBTITLE_LOADING
  };
};

export const getJobTitleSuccess = payload => {
  return {
    type: types.GET_JOBTITLE_SUCCESS,
    payload
  };
};

export const getJobTitleFailure = error => {
  return {
    type: types.GET_JOBTITLE_FAILURE,
    payload: error
  };
};

/* End title/category Suggestion  */

function moveLatestTitlesToTop(jobTitles = [], latestJobTitle = []) {
  // Filter out titles that are present in latestTitles and move them to the top
  const latestTitles = latestJobTitle.map(job => job.title);

  // Filter out titles that are present in latestTitles and move them to the top
  const reorderedJobTitles = [
    ...latestJobTitle,
    ...jobTitles.filter(job => !latestTitles.includes(job.title))
  ];
  return reorderedJobTitles;
}

export const getLatestJobTitle = () => {
  return (dispatch, store) => {
    JobPostingService.getLatestJobTitle()
      .then(response => {
        const reduxStore = store();
        const { jobPostingReducer = {} } = reduxStore || {};

        const {
          searchJobTitle: {
            payload: { job_titles = {} }
          }
        } = jobPostingReducer;

        const {
          data: { job_titles: latestJobTitle = {} }
        } = response.data;

        const { options: jobTitlesArray = [] } = job_titles;
        const { options: latestJobTitleArray = [] } = latestJobTitle;

        const updatedLatestJobTitleArray = latestJobTitleArray.map(item => {
          return {
            ...item,
            is_history: true
          };
        });

        const updatedOptions = moveLatestTitlesToTop(
          jobTitlesArray,
          updatedLatestJobTitleArray
        );

        const updatedTitles = {
          job_titles: {
            ...jobPostingReducer.searchJobTitle.payload.job_titles,
            options: updatedOptions,
            is_history: true
          }
        };
        dispatch(getJobTitleSuccess(updatedTitles));
      })
      .catch(error => {
        dispatch(showSnackBar(ERROR_MESSAGE));
      });
  };
};

/* Start Suggested JD */

export const getSuggestedJD = ({ catId, cityId }) => {
  return async (dispatch, store) => {
    return new Promise(async (resolve, reject) => {
      dispatch(getSuggestedJDLoading());

      const cache = await checkInCache(
        { category_id: catId },
        STORAGE_ITEMS.JD_METADATA_CACHE
      );

      if (!cache) {
        makeAjaxRequest({
          method: "GET",
          url: fetchUrl("metadata", "jd-metadata"),
          queryParams: { category_id: catId, city_id: cityId },
          auth: false
        })
          .then(response => {
            const { data: jdMetadata } = response.data;

            // CacheService.addToCache({
            //   data: { key: { category_id: catId }, value: jdMetadata },
            //   cacheName: STORAGE_ITEMS.JD_METADATA_CACHE,
            //   storageType: STORAGE_KEYS.LOCAL_STORAGE,
            //   ttl: 720
            // });

            dispatch(getSuggestedJDSuccess(jdMetadata));
            resolve(jdMetadata);
          })
          .catch(error => {
            dispatch(getSuggestedJDFailure(error.message));
            dispatch(showSnackBar(ERROR_MESSAGE));
            reject(error);
          });
      } else {
        dispatch(getSuggestedJDSuccess(cache));
        resolve(cache);
      }
    });
  };
};

export const getSuggestedJDLoading = () => {
  return {
    type: types.GET_JD_LOADING
  };
};

export const getSuggestedJDSuccess = payload => {
  return {
    type: types.GET_JD_SUCCESS,
    payload: payload
  };
};

export const getSuggestedJDFailure = error => {
  return {
    type: types.GET_JD_FAILURE,
    payload: error
  };
};

/* End Suggested JD  */

/* Start Job Edited Meta Fields */

export const getEditedJobFields = ({
  isJobRePost = false,
  isLoader = true,
  jobId
}) => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      if (jobId) {
        if (isLoader) dispatch(getEditedFieldsLoading());

        const cache = CacheService.checkInCache({
          key: STORAGE_ITEMS.JOB_FIELDS_CACHE,
          cacheName: STORAGE_ITEMS.JOB_FIELDS_CACHE,
          storageType: STORAGE_KEYS.LOCAL_STORAGE
        });

        if (!cache || cache?.is_repost !== isJobRePost) {
          makeAjaxRequest({
            method: "GET",
            url: fetchUrl("metadata", "job_fields"),
            auth: false,
            queryParams: { is_repost: isJobRePost }
          })
            .then(response => {
              const { data: jobFieldsData } = response.data;

              CacheService.addToCache({
                data: {
                  key: STORAGE_ITEMS.JOB_FIELDS_CACHE,
                  value: { ...jobFieldsData, is_repost: isJobRePost }
                },
                cacheName: STORAGE_ITEMS.JOB_FIELDS_CACHE,
                storageType: STORAGE_KEYS.LOCAL_STORAGE,
                ttl: 720
              });

              dispatch(getEditedFieldsSuccess(jobFieldsData));

              resolve(response.data.data);
            })
            .catch(error => {
              dispatch(getEditedFieldsFailure(error.message));
              dispatch(showSnackBar(ERROR_MESSAGE));
              reject(error);
            });
        } else {
          dispatch(getEditedFieldsSuccess(cache));
          resolve(true);
        }
      } else {
        resolve(true);
      }
    });
  };
};

export const getEditedFieldsLoading = () => {
  return {
    type: types.GET_EDITED_JOB_FIELD_LOADING
  };
};

export const getEditedFieldsSuccess = payload => {
  return {
    type: types.GET_EDITED_JOB_FIELD_SUCCESS,
    payload: payload
  };
};

export const getEditedFieldsFailure = error => {
  return {
    type: types.GET_EDITED_JOB_FIELD_FAILURE,
    payload: error
  };
};

export const checkFirstJob = value => {
  return dispatch => {
    dispatch({
      type: types.IS_FIRST_JOB,
      payload: value
    });
  };
};
/* End Job Edited Meta Fields */

/* Start Contextual Job Posting Fields */

export const getContextualFields = category_id => {
  return async dispatch => {
    return new Promise((resolve, reject) => {
      dispatch(getContextualFieldsLoading());

      // const cache = await checkInCache(
      //   { category_id },
      //   STORAGE_ITEMS.JOB_POST_FIELDS_CACHE
      // );
      const cache = false;
      if (!cache) {
        makeAjaxRequest({
          method: "GET",
          url: fetchUrl("v2/metadata", "job_post_fields"),
          queryParams: { category_id: category_id },
          auth: false
        })
          .then(response => {
            const { data } = response.data;
            if (data && data.job_post_fields) {
              CacheService.addToCache({
                data: { key: { category_id }, value: data.job_post_fields },
                cacheName: STORAGE_ITEMS.JOB_POST_FIELDS_CACHE,
                storageType: STORAGE_KEYS.LOCAL_STORAGE,
                ttl: 720
              });

              dispatch(getContextualFieldsSuccess(data.job_post_fields));
              resolve(data.job_post_fields);
            }
          })
          .catch(error => {
            dispatch(getContextualFieldsFailure(error.message));
            dispatch(showSnackBar(ERROR_MESSAGE));
            reject(error);
          });
      } else {
        dispatch(getContextualFieldsSuccess(cache));
        resolve(cache);
      }
    });
  };
};

export const getContextualFieldsLoading = () => {
  return {
    type: types.GET_FORM_FIELDS_LOADING
  };
};

export const getContextualFieldsSuccess = payload => {
  return {
    type: types.GET_FORM_FIELDS_SUCCESS,
    payload
  };
};

export const getContextualFieldsFailure = error => {
  return {
    type: types.GET_FORM_FIELDS_FAILURE,
    payload: error
  };
};

/* End Contextual Job Posting Fields */

/* GET email Verification link API Starts */

export const sendVerificationEmail = (jobId, isResend, isVerifyNow) => {
  return dispatch => {
    dispatch(sendVerificationEmailLoading());
    makeAjaxRequest({
      method: "GET",
      url: fetchUrl("recruiter", "v2/send_verification_email"),
      auth: true
    })
      .then(response => {
        dispatch(sendVerificationEmailSuccess(response.data.data));
        if (jobId) {
          Router.push({
            pathname: EMAIL_VERIFICATION,
            query: { job_id: jobId }
          });
        }
        if (isResend) {
          dispatch(showSuccessSnackBar(response.data.message));
        }
        if (isVerifyNow) {
          dispatch(showSuccessSnackBar(response.data.message));
          Router.push(EMAIL_VERIFICATION);
        }
      })
      .catch(error => {
        let errorMessage = "";
        errorMessage = getErrorMessage(error);
        dispatch(sendVerificationEmailFailure(errorMessage));
        dispatch(showSnackBar(errorMessage));
      });
  };
};

export const sendVerificationEmailLoading = () => {
  return {
    type: types.SEND_VERIFICATION_EMAIL_LOADING
  };
};

export const sendVerificationEmailSuccess = payload => {
  return {
    type: types.SEND_VERIFICATION_EMAIL_SUCCESS,
    payload
  };
};

export const sendVerificationEmailFailure = error => {
  return {
    type: types.SEND_VERIFICATION_EMAIL_FAILURE,
    payload: error
  };
};

/* GET email Verification link API Starts */

/* Add Client API starts */

export const addClientLoading = () => {
  return {
    type: types.ADD_CLIENT_LOADING
  };
};

export const addClientSuccess = payload => {
  return {
    type: types.ADD_CLIENT_SUCCESS,
    payload
  };
};

export const addClientFailure = error => {
  return {
    type: types.ADD_CLIENT_FAILURE,
    payload: error
  };
};

export const addClient = data => {
  const { jobId, clientName, clientAddress, callback } = data;
  return dispatch => {
    dispatch(addClientLoading());
    makeAjaxRequest({
      method: "POST",
      url: fetchUrl("recruiter", "add-client"),
      data: {
        job_id: jobId,
        client_name: clientName,
        client_address: clientAddress
      }
    })
      .then(response => {
        dispatch(addClientSuccess(response.data.data));
        const { tab = "live" } = response.data.data;
        let activeTab = activeTabFormat(tab);
        dispatch(setActiveTab(activeTab));
        if (callback) {
          callback();
        }
      })
      .catch(error => {
        let errorMessage = "";
        errorMessage = getErrorMessage(error);
        dispatch(addClientFailure(errorMessage));
        dispatch(showSnackBar(errorMessage));
      });
  };
};

/* Add Client API ends */

/* Update Client API starts */

export const updateClient = (jobId, clientId, clientName, clientAddress) => {
  return dispatch => {
    dispatch(updateClientLoading());
    makeAjaxRequest({
      method: "POST",
      url: fetchUrl("recruiter", "update-client"),
      data: {
        job_id: jobId,
        client_id: clientId,
        client_name: clientName,
        client_address: clientAddress
      }
    })
      .then(response => {
        dispatch(updateClientSuccess(response.data.data));
      })
      .catch(error => {
        let errorMessage = "";
        errorMessage = getErrorMessage(error);
        dispatch(updateClientFailure(errorMessage));
        dispatch(showSnackBar(errorMessage));
      });
  };
};

export const updateClientLoading = () => {
  return {
    type: types.UPDATE_CLIENT_LOADING
  };
};

export const updateClientSuccess = payload => {
  return {
    type: types.UPDATE_CLIENT_SUCCESS,
    payload
  };
};

export const updateClientFailure = error => {
  return {
    type: types.UPDATE_CLIENT_FAILURE,
    payload: error
  };
};

/* Update Client API ends */

/* Start OTP phone verification */
export const changePhoneSendOTP = (phone, callback) => {
  return dispatch => {
    dispatch(changePhoneSendOTPLoading());
    makeAjaxRequest({
      method: "POST",
      url: fetchUrl("recruiter", "send_otp_to_phone"),
      data: { is_four_digit: true, phone }
    })
      .then(response => {
        dispatch(changePhoneSendOTPSuccess(response.data.data));
        if (response.data.message)
          dispatch(showSuccessSnackBar(response.data.message));
      })
      .then(() => {
        if (callback) {
          callback();
        }
      })
      .catch(error => {
        let errorMessage = "";
        errorMessage = getErrorMessage(error);
        dispatch(showSnackBar(errorMessage));
        dispatch(changePhoneSendOTPFailure(error));
      });
  };
};

export const changePhoneSendOTPLoading = () => {
  return {
    type: types.CHANGE_PHONE_SEND_OTP_LOADING
  };
};

export const changePhoneSendOTPSuccess = payload => {
  return {
    type: types.CHANGE_PHONE_SEND_OTP_SUCCESS,
    payload
  };
};

export const changePhoneSendOTPFailure = error => {
  return {
    type: types.CHANGE_PHONE_SEND_OTP_FAILURE,
    payload: error
  };
};

export const changePhoneVerifyOTP = (phone, otp, callback) => {
  return dispatch => {
    dispatch(changePhoneVerifyOTPLoading());
    makeAjaxRequest({
      method: "POST",
      url: fetchUrl("recruiter", "verify_otp_phone"),
      data: { is_four_digit: true, phone, otp }
    })
      .then(response => {
        dispatch(changePhoneVerifyOTPSuccess(response.data.data));
        if (response.data.message)
          dispatch(showSuccessSnackBar(response.data.message));
      })
      .then(() => {
        callback();
      })
      .catch(error => {
        let errorMessage = "";
        errorMessage = getErrorMessage(error);
        dispatch(showSnackBar(errorMessage));
        dispatch(changePhoneVerifyOTPFailure(error));
      });
  };
};

export const changePhoneVerifyOTPLoading = () => {
  return {
    type: types.CHANGE_PHONE_VERIFY_OTP_LOADING
  };
};

export const changePhoneVerifyOTPSuccess = payload => {
  return {
    type: types.CHANGE_PHONE_VERIFY_OTP_SUCCESS,
    payload
  };
};
export const changePhoneVerifyOTPFailure = error => {
  return {
    type: types.CHANGE_PHONE_VERIFY_OTP_FAILURE,
    payload: error
  };
};
/* End OTP phone verification*/

export const getMetaDataVersionSuccess = payload => {
  return {
    type: types.GET_META_DATA_VER_SUCCESS,
    payload: payload
  };
};

function modifyVersions(versions) {
  const obj = {};
  versions.forEach(item => {
    obj[item.name] = item.version;
  });
  return obj;
}

export const getMetaDataVersion = () => {
  return (dispatch, store) => {
    const { jobPostingReducer = {} } = store() || {};
    const { metaDataVersion = {} } = jobPostingReducer;
    if (metaDataVersion.isSuccess && !checkSessionChanged(metaDataVersion)) {
      return dispatch(
        getMetaDataVersionSuccess(
          JSON.parse(JSON.stringify(metaDataVersion.payload))
        )
      );
    }
    MetaDataService.getMetadataVersion()
      .then(response => {
        dispatch(
          getMetaDataVersionSuccess({
            ...modifyVersions(response.data.data.versions.options)
          })
        );
      })
      .catch(error => {
        // dispatch(getMetaDataFailure(error.message));
        // dispatch(showSnackBar(ERROR_MESSAGE));
      });
  };
};

/* End Get Job Live Config */

/* Start Get organisations list */
//
export const getOrganisations = company => {
  return dispatch => {
    dispatch(getOrganisationsLoading());
    makeAjaxRequest({
      method: "GET",
      url: fetchUrl("suggestions", "organisations"),
      queryParams: { query_string: company }
    })
      .then(response => {
        const { data } = response.data;
        dispatch(getOrganisationsSuccess(data));
      })
      .catch(error => {
        let errorMessage = "";
        errorMessage = getErrorMessage(error);
        dispatch(showSnackBar(errorMessage));
        dispatch(getOrganisationsFailure(error));
      });
  };
};

export const getOrganisationsLoading = () => {
  return {
    type: types.GET_ORGANISATIONS_LOADING
  };
};

export const getOrganisationsSuccess = payload => {
  return {
    type: types.GET_ORGANISATIONS_SUCCESS,
    payload
  };
};
export const getOrganisationsFailure = error => {
  return {
    type: types.GET_ORGANISATIONS_FAILURE,
    payload: error
  };
};

export const resetOrganisationData = () => {
  return dispatch => {
    dispatch(getOrganisationsSuccess([]));
  };
};
/* End Get Get organisations list */

export const getAverageSalaryLoading = () => {
  return {
    type: types.GET_AVG_SALARY_LOADING
  };
};

export const getAverageSalarySuccess = payload => {
  return {
    type: types.GET_AVG_SALARY_SUCCESS,
    payload
  };
};

export const getAverageSalaryFailure = error => {
  return {
    type: types.GET_AVG_SALARY_FAILURE,
    payload: error
  };
};

export const getAverageSalary = ({ cityId, categoryId }) => {
  return dispatch => {
    dispatch(getAverageSalaryLoading());
    JobPostingService.getAverageSalary({ cityId, categoryId })
      .then(response => {
        const { data } = response.data;
        dispatch(getAverageSalarySuccess(data.average_salary));
      })
      .catch(error => {
        dispatch(getAverageSalaryFailure(error.message));
        dispatch(showSnackBar(ERROR_MESSAGE));
      });
  };
};
